/**
 * Lista de instalações elétricas (Prontuário NR-10, #364): filtros em linha, situação calculada pelo
 * servidor, última inspeção, próxima prevista e documentos de cada instalação.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  ControleIconButton,
  DataTable,
  InlineFilterBar,
  InlineSelect,
  PageShell,
  RouteActionButton,
  type DataTableColumn,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useListaPermitida } from '../../authorization/useListaPermitida';
import { useAcao, useControle } from '../../authorization/useVeredito';
import { useFilterState } from '../../hooks/useFilterState';
import { departmentService } from '../../services/departmentService';
import {
  electricalInstallationService,
  ROTULO_DA_CLASSE_DE_TENSAO,
  ROTULO_DA_SITUACAO_DA_INSTALACAO,
  ROTULO_DO_TIPO_DE_INSTALACAO,
  type ElectricalInstallation,
  type ElectricalInstallationFilters,
  type ElectricalInstallationSituation,
} from '../../services/electricalInstallationService';
import { OverdueChip, PlainChip, ResultChip, SituationChip } from './components/ElectricalChips';

/** Os setores do filtro; a lista só é pedida a quem pode ler setores (`useListaPermitida`). */
const loadDepartmentOptions = async (): Promise<readonly { value: string; label: string }[]> => {
  const response = await departmentService.getDepartments({ page: 1, limit: 1000 });
  return response.data.map((department) => ({ value: String(department.id), label: department.name }));
};

const SITUATION_OPTIONS = (
  Object.keys(ROTULO_DA_SITUACAO_DA_INSTALACAO) as ElectricalInstallationSituation[]
).map((situation) => ({ value: situation, label: ROTULO_DA_SITUACAO_DA_INSTALACAO[situation] }));

/** `2026-11-30` → `30/11/2026`, sem passar por `Date` (que deslocaria o dia pelo fuso). */
const formatDate = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

const formatVolts = (volts: number): string => `${volts.toLocaleString('pt-BR')} V`;

interface InstallationFiltersData {
  search: string;
  departmentId: string;
  situation: string;
}

const DEFAULT_FILTERS: InstallationFiltersData = { search: '', departmentId: '', situation: '' };

export const ElectricalInstallationListPage: React.FC = () => {
  const navigate = useNavigate();
  const ver = useControle('/electrical-installations/:id', pode('electrical_installation.read'), 'Ver');
  const editar = useControle('/electrical-installations/:id', pode('electrical_installation.update'), 'Editar');
  const excluir = useAcao('electrical_installation.delete', 'Excluir');
  const criar = useAcao('electrical_installation.create', 'Cadastrar a primeira instalação');

  const [installations, setInstallations] = useState<ElectricalInstallation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [apiFilters, setApiFilters] = useState<ElectricalInstallationFilters>({});
  const [deleteTarget, setDeleteTarget] = useState<ElectricalInstallation | null>(null);
  const [deleting, setDeleting] = useState(false);
  const departmentList = useListaPermitida('department.read', 'Setores', loadDepartmentOptions);
  const departments = useMemo(() => [...departmentList.itens], [departmentList.itens]);

  const loadInstallations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await electricalInstallationService.getInstallations({
        page: page + 1,
        limit: rowsPerPage,
        ...apiFilters,
      });
      setInstallations(response.installations);
      setTotal(response.total);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar as instalações elétricas');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, apiFilters]);

  useEffect(() => {
    loadInstallations();
  }, [loadInstallations]);

  const handleFilter = useCallback((data: InstallationFiltersData) => {
    const filters: ElectricalInstallationFilters = {};
    if (data.search) filters.search = data.search;
    if (data.departmentId) filters.departmentId = Number(data.departmentId);
    if (data.situation) filters.situation = data.situation as ElectricalInstallationSituation;
    setApiFilters(filters);
    setPage(0);
  }, []);

  const { filters, setFilter, clearAll, activeChips } = useFilterState(DEFAULT_FILTERS, {
    onFilter: handleFilter,
    applyOnMount: true,
    live: true,
    labels: { departmentId: 'Setor', situation: 'Situação' },
    formatValue: {
      departmentId: (value: string) => departments.find((option) => option.value === value)?.label ?? value,
      situation: (value: string) => SITUATION_OPTIONS.find((option) => option.value === value)?.label ?? value,
    },
  });

  const filtersActive = Object.keys(apiFilters).length > 0;

  const closeDeleteDialog = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await electricalInstallationService.deleteInstallation(deleteTarget.id);
      closeDeleteDialog();
      await loadInstallations();
    } catch (err: unknown) {
      // O servidor recusa a exclusão de quem ainda tem registros ou arquivos e diz quantos de cada.
      setError(err instanceof Error ? err.message : 'Não foi possível excluir a instalação.');
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  const departmentName = useCallback(
    (departmentId: number | null): string =>
      departmentId === null
        ? 'Sem setor'
        : (departments.find((option) => option.value === String(departmentId))?.label ?? '—'),
    [departments]
  );

  const openInstallation = useCallback(
    (installation: ElectricalInstallation) => navigate(`/electrical-installations/${installation.id}`),
    [navigate]
  );

  // Ícones de 44px: o `actions` do DataTable é comum e fixa 30px, então a coluna de ações é daqui.
  const columns = useMemo<DataTableColumn<ElectricalInstallation>[]>(
    () => [
      {
        id: 'name',
        label: 'Instalação',
        primary: true,
        render: (installation) => (
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {installation.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {[installation.tag, departmentName(installation.departmentId)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
        ),
      },
      {
        id: 'type',
        label: 'Tipo',
        render: (installation) => ROTULO_DO_TIPO_DE_INSTALACAO[installation.installationType],
      },
      {
        id: 'voltage',
        label: 'Tensão',
        render: (installation) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
            <span>{installation.nominalVoltage === null ? '—' : formatVolts(installation.nominalVoltage)}</span>
            <PlainChip label={ROTULO_DA_CLASSE_DE_TENSAO[installation.summary.voltageClass]} />
          </Box>
        ),
      },
      {
        id: 'lastInspection',
        label: 'Última inspeção',
        render: ({ summary }) =>
          summary.lastInspection ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
              <span>{formatDate(summary.lastInspection.performedAt)}</span>
              <ResultChip result={summary.lastInspection.result} />
            </Box>
          ) : (
            <PlainChip label="Sem inspeção" />
          ),
      },
      {
        id: 'nextDue',
        label: 'Próxima prevista',
        render: ({ summary }) => {
          if (!summary.nextDueDate) return '—';
          return summary.overdue ? (
            <OverdueChip label={`Vencida · ${formatDate(summary.nextDueDate)}`} />
          ) : (
            formatDate(summary.nextDueDate)
          );
        },
      },
      { id: 'documents', label: 'Documentos', align: 'right', render: ({ summary }) => summary.documentCount },
      { id: 'situation', label: 'Situação', render: ({ summary }) => <SituationChip situation={summary.situation} /> },
      {
        id: 'actions',
        label: 'Ações',
        align: 'center',
        render: (installation) => {
          const canEdit = !editar.disabled;
          const abrir = canEdit ? editar : ver;
          return (
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
              <ControleIconButton
                controle={abrir}
                onClick={() => openInstallation(installation)}
                sx={{ width: 44, height: 44 }}
              >
                {canEdit ? <EditIcon /> : <ViewIcon />}
              </ControleIconButton>
              {!excluir.disabled && (
                <ControleIconButton
                  controle={excluir}
                  color="error"
                  onClick={() => setDeleteTarget(installation)}
                  sx={{ width: 44, height: 44 }}
                >
                  <DeleteIcon />
                </ControleIconButton>
              )}
            </Box>
          );
        },
      },
    ],
    [departmentName, editar, excluir, openInstallation, ver]
  );

  const goToNewInstallation = () => navigate('/electrical-installations/new');

  return (
    <PageShell
      breadcrumbs={[{ label: 'Início', path: '/' }, { label: 'Instalações elétricas' }]}
      title="Instalações elétricas"
      subtitle="O prontuário de cada empresa: instalações, inspeções, manutenções e documentos técnicos."
      actions={
        <RouteActionButton
          para="/electrical-installations/new"
          exige={pode('electrical_installation.create')}
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
        >
          Nova instalação
        </RouteActionButton>
      }
    >
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <InlineFilterBar
          searchPlaceholder="Nome, identificação ou localização"
          searchValue={filters.search}
          onSearchChange={(value) => setFilter('search', value)}
          resultCount={total}
          resultNoun="instalações"
          chips={activeChips}
          onClearAll={clearAll}
          loading={loading}
        >
          <Tooltip title={departmentList.bloqueio ?? ''}>
            <span>
              <InlineSelect
                label="Setor"
                value={filters.departmentId}
                options={departments}
                onChange={(value) => setFilter('departmentId', value)}
                emptyLabel="Todos"
                minWidth={180}
                disabled={!departmentList.permitido}
              />
            </span>
          </Tooltip>
          <InlineSelect
            label="Situação"
            value={filters.situation}
            options={SITUATION_OPTIONS}
            onChange={(value) => setFilter('situation', value)}
            emptyLabel="Todas"
            minWidth={180}
          />
        </InlineFilterBar>
      </Box>

      <DataTable
        columns={columns}
        rows={installations}
        getRowId={(installation) => installation.id}
        loading={loading}
        filtersActive={filtersActive}
        onClearFilters={clearAll}
        emptyState={{
          title: 'Nenhuma instalação elétrica cadastrada para esta empresa',
          description:
            'O Laudo NR 10 lista cada instalação com suas inspeções, manutenções e documentos técnicos. Comece cadastrando a primeira; depois registre a última inspeção e anexe o diagrama unifilar.',
          action: {
            label: 'Cadastrar a primeira instalação',
            node: 'electrical-installations',
            deniedHint: 'Peça a um administrador para cadastrar as instalações elétricas desta empresa.',
            onClick: goToNewInstallation,
            allowed: !criar.disabled,
          },
        }}
        pagination={{
          page,
          rowsPerPage,
          totalCount: total,
          onPageChange: setPage,
          onRowsPerPageChange: (value) => {
            setRowsPerPage(value);
            setPage(0);
          },
        }}
      />

      <Dialog open={Boolean(deleteTarget)} onClose={closeDeleteDialog} aria-labelledby="electrical-delete-title">
        <DialogTitle id="electrical-delete-title">Excluir instalação</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1rem' }}>
            Tem certeza que deseja excluir {deleteTarget ? `"${deleteTarget.name}"` : 'esta instalação'}? Instalação com
            inspeções, manutenções ou documentos não pode ser excluída: o sistema avisa quantos de cada precisam sair
            antes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="contained" onClick={closeDeleteDialog} sx={{ minHeight: 44 }} autoFocus>
            Cancelar
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            sx={{ minHeight: 44 }}
          >
            Excluir instalação
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default ElectricalInstallationListPage;
